import React, { FC, useState } from "react";

interface InputBaseProps {
    value: string;
    onChange: (value: string) => void;
    isDisabled: boolean;
    minLength: number;
    maxLength: number;
    placeholder: string;
}

interface InputErrorState {
    showError: boolean;
    errorMessage: string | null;
    isFocused: boolean;
    setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
    isTouched: boolean;
    setIsTouched: React.Dispatch<React.SetStateAction<boolean>>;
}

const useInputValidation = (
    value: string,
    minLength: number,
    maxLength: number,
    fieldName: string
): InputErrorState => {
    const [isTouched, setIsTouched] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const getErrorMessage = (value: string): string | null => {
        if (value.length === 0) return null;
        if (value.length < minLength) return `Your ${fieldName} should be at least ${minLength} characters long.`;
        if (value.length > maxLength) return `Your ${fieldName} should not exceed ${maxLength} characters.`;
        return null;
    };

    const errorMessage = getErrorMessage(value);
    const showError = Boolean(errorMessage) && !isFocused && isTouched;

    return {
        showError,
        errorMessage,
        isFocused,
        setIsFocused,
        isTouched,
        setIsTouched
    };
};

const ErrorIcon = () => (
    <div className="absolute top-0 end-0 flex items-center pointer-events-none p-3">
        <svg className="shrink-0 size-4 text-red-500" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" x2="12" y1="8" y2="12"></line>
            <line x1="12" x2="12.01" y1="16" y2="16"></line>
        </svg>
    </div>
);

export const DescriptionInput: FC<InputBaseProps> = ({
    value,
    onChange,
    isDisabled,
    minLength,
    maxLength,
    placeholder
}) => {
    const {
        showError,
        errorMessage,
        isFocused,
        setIsFocused,
        isTouched,
        setIsTouched
    } = useInputValidation(value, minLength, maxLength, "description");

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        if (newValue.length <= maxLength) {
            onChange(newValue.replace(/\n/g, '\n')); // Preserve line breaks
        }
        if (!isTouched) setIsTouched(true);
    };

    return (
        <div className="mb-0">
            <span className="text-lg font-medium border-gray-300">
                <div className="text-left mb-2 sm:mb-0 ml-2 py-2">
                    Description
                </div>
            </span>
            <div className="relative">
                <textarea
                    autoComplete="off"
                    id="description-input"
                    className={`py-3 px-4 block w-full border ${showError ? 'border-red-500' : 'border-gray-200'} rounded-lg resize-none text-base focus:border-blue-500 focus:ring-blue-500`}
                    placeholder={placeholder}
                    value={value}
                    rows={8}
                    onChange={handleInputChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    disabled={isDisabled}
                />
                {showError && <ErrorIcon />}
            </div>
            <div className="h-6 mt-1">
                {showError && (
                    <p className="text-sm text-red-600 text-left pl-2" id="description-error-helper">
                        {errorMessage}
                    </p>
                )}
            </div>
        </div>
    );
};

interface DescriptionStepProps {
    savedDescription?: string | null;
    onDescriptionChange: (description: string) => void;
    isDisabled: boolean;
}

export const DescriptionStep: React.FC<DescriptionStepProps> = ({
    savedDescription = '',
    onDescriptionChange,
    isDisabled
}) => {
    return (
        <div className="w-full max-w-[1000px] mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <h1 className="block text-2xl font-bold text-gray-800 sm:text-4xl mb-5">
                Add the final details
            </h1>
            <p className="mt text-lg text-gray-800 dark:text-neutral-400 mb-12">
                This is your chance to really show what's special about your listing.
            </p>

            <DescriptionInput
                value={savedDescription || ''}
                onChange={onDescriptionChange}
                isDisabled={isDisabled}
                minLength={30}
                maxLength={5000}
                placeholder={"Describe the property, including the unique selling points...\n\nIntroduce yourself to your potential flatmate...\n\nDescribe the ideal flatmate you're looking for..."}
            />
        </div>
    );
};